import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { WorkflowCanvasCustomEvent } from 'dashboard/components/WorkflowEditor/WorkflowCanvas/constants';
import {
  edgeComponents,
  edgesFromGraph,
} from 'dashboard/components/WorkflowEditor/WorkflowCanvas/edges';
import { ClickPayloadType } from 'dashboard/components/WorkflowEditor/WorkflowCanvas/NodePlusButton';
import {
  nodeComponents,
  nodesFromGraph,
} from 'dashboard/components/WorkflowEditor/WorkflowCanvas/nodes';
import {
  buildStepGraph,
  StepGraphExtraType,
} from 'dashboard/components/WorkflowEditor/WorkflowCanvas/step-graph';
import useCopyWorkflowSteps from 'dashboard/components/WorkflowEditor/WorkflowCanvas/useCopyWorkflowSteps';
import useModuleToBlockStepsConfigQuery from 'dashboard/components/WorkflowEditor/WorkflowCanvas/useModuleToBlockStepsConfigQuery';
import {
  buildRenderingRunContext,
  distinctNestedNodeTokens,
} from 'dashboard/components/WorkflowEditor/WorkflowCanvas/utils';
import FlowPlotter, { useSetNodeToPan } from 'dashboard/components-lib/FlowPlotter';
import { flattenSteps } from 'dashboard/lib/workflows';
import { WorkflowRunStep } from 'dashboard/models/workflow-step';
import {
  WorkflowStep,
  WorkflowStepInput,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';
import { LocalStorageKey } from 'lib/clients/StorageClient';
import useKeyPressToggle from 'lib/hooks/useKeyPressToggle';
import useLocalStorage from 'lib/hooks/useLocalStorage';
import useOnMount from 'lib/hooks/useOnMount';
import { findPath } from 'lib/workflows';

const CanvasContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

interface WorkflowCanvasProps {
  editable: boolean;
  deactivatedNodeTokens?: string[];
  erroredNodeTokens: string[];
  focusedNodeToken: string | null;
  inputStep: WorkflowStepInput;
  moduleRunSteps?: Record<string, WorkflowRunStep[]>; // module token -> run steps
  runLoopTokensToFocusedCycle?: Record<string, number>;
  runSteps?: WorkflowRunStep[] | null;
  selectedNodeToken: string | null;
  selectedModuleToken?: string | null;
  showRunStats?: boolean;
  steps: WorkflowStep[];
  addNodeAfter: (token: string) => void;
  addNodeAfterBranchStart: (parentToken: string, branchIndex: number) => void;
  moveStep: (
    targetStepToken: string,
    previousOrParentOfDestinationNodeToken: string,
    destinationBranchIndex?: number,
  ) => void;
  selectNode: (token: string, context: { moduleToken: string | null }) => void;
}

const WorkflowCanvas: FC<WorkflowCanvasProps> = ({
  deactivatedNodeTokens = [],
  editable,
  erroredNodeTokens,
  focusedNodeToken,
  inputStep,
  moduleRunSteps,
  runLoopTokensToFocusedCycle = {},
  runSteps = null,
  selectedNodeToken,
  selectedModuleToken = null,
  showRunStats = false,
  steps,
  addNodeAfter,
  addNodeAfterBranchStart,
  moveStep,
  selectNode,
}) => {
  const [expandedModuleStepTokens, updateExpandedModuleStepTokens] = useState<string[]>([]);
  const panTo = useSetNodeToPan();
  const [persistedCollapsedBranchRootTokens, persistCollapsedBranchRootTokens] = useLocalStorage<
    Record<string, string[]>
  >(LocalStorageKey.DashboardWorkflowEditorCollapsedSteps, {});

  const collapsedBranchRootTokens = useMemo(
    () => persistedCollapsedBranchRootTokens[inputStep.token] ?? [],
    [persistedCollapsedBranchRootTokens, inputStep.token],
  );

  const toggleCollapsedNode = useCallback(
    (targetNodeToken: string) => {
      persistCollapsedBranchRootTokens({
        ...persistedCollapsedBranchRootTokens,
        [inputStep.token]:
          collapsedBranchRootTokens.indexOf(targetNodeToken) === -1
            ? [...collapsedBranchRootTokens, targetNodeToken]
            : collapsedBranchRootTokens.filter((token) => token !== targetNodeToken),
      });
    },
    [
      collapsedBranchRootTokens,
      persistCollapsedBranchRootTokens,
      persistedCollapsedBranchRootTokens,
      inputStep.token,
    ],
  );

  const moduleToBlockSteps = useModuleToBlockStepsConfigQuery(steps);
  const prefixedModuleBlockSteps = useMemo(
    () => distinctNestedNodeTokens(moduleToBlockSteps),
    [moduleToBlockSteps],
  );

  const keyedGraph = useMemo(
    () =>
      buildStepGraph({
        steps,
        inputStep,
        collapsedNodeTokens: [...collapsedBranchRootTokens, ...expandedModuleStepTokens],
        moduleTokenToBlockSteps: prefixedModuleBlockSteps,
      }),
    [
      steps,
      inputStep,
      collapsedBranchRootTokens,
      expandedModuleStepTokens,
      prefixedModuleBlockSteps,
    ],
  );

  const [, { copy: copySteps }] = useCopyWorkflowSteps();

  const { tokenToRunStepDisplay, tokensForActiveLoopBack } = useMemo(() => {
    if (runSteps == null) return { tokenToRunStepDisplay: {}, tokensForActiveLoopBack: [] };

    return buildRenderingRunContext({
      enableRunStats: showRunStats,
      keyedGraph,
      lastStepToken: steps.slice(-1)[0]?.token ?? '',
      runLoopTokensToFocusedCycle,
      runSteps,
      moduleRunSteps: moduleRunSteps ?? {},
      inputStepToken: inputStep.token,
    });
  }, [
    runLoopTokensToFocusedCycle,
    runSteps,
    moduleRunSteps,
    keyedGraph,
    inputStep.token,
    steps,
    showRunStats,
  ]);

  const { nodes, edges } = useMemo(
    () => ({
      nodes: nodesFromGraph(keyedGraph, inputStep.token, {
        deactivatedNodeTokens,
        editable,
        erroredNodeTokens,
        expandedModuleStepTokens,
        focusedNodeToken,
        selectedNodeToken: [
          selectedModuleToken != null && selectedModuleToken !== selectedNodeToken
            ? `${selectedModuleToken}-`
            : '',
          selectedNodeToken,
        ].join(''),
        tokenToRunStepDisplay,
      }),
      edges: edgesFromGraph(keyedGraph, { tokenToRunStepDisplay, tokensForActiveLoopBack }),
    }),
    [
      keyedGraph,
      inputStep.token,
      deactivatedNodeTokens,
      editable,
      erroredNodeTokens,
      expandedModuleStepTokens,
      focusedNodeToken,
      selectedNodeToken,
      selectedModuleToken,
      tokensForActiveLoopBack,
      tokenToRunStepDisplay,
    ],
  );

  useOnMount(() => {
    if (focusedNodeToken) {
      const pathToFocusedNodeTokens = new Set(
        findPath(focusedNodeToken, steps).map((workflowStep) => workflowStep.token),
      );

      // uncollapse nodes as necessary to pan to focused node token
      persistCollapsedBranchRootTokens({
        ...persistedCollapsedBranchRootTokens,
        [inputStep.token]: collapsedBranchRootTokens.filter(
          (token) => !pathToFocusedNodeTokens.has(token),
        ),
      });
    }
  });

  useEffect(() => {
    focusedNodeToken && keyedGraph[focusedNodeToken] && panTo(focusedNodeToken);
  }, [focusedNodeToken, keyedGraph, panTo]);

  const isShiftPressed = useKeyPressToggle('Shift');

  return (
    <CanvasContainer>
      <FlowPlotter
        edgeComponents={edgeComponents}
        edges={edges}
        nodeComponents={nodeComponents}
        nodes={nodes}
        onDragAndDrop={({ droppedNodeId, draggedNodeId }) => {
          const destination = keyedGraph[droppedNodeId];
          if (
            destination?.step?.type === StepGraphExtraType.PlusButton &&
            !destination.adjacents.includes(draggedNodeId) &&
            !keyedGraph[draggedNodeId].adjacents.includes(droppedNodeId)
          ) {
            if (destination.step.clickPayload.type === ClickPayloadType.AfterStep) {
              moveStep(draggedNodeId, destination.step.clickPayload.previousNodeToken);
            } else {
              moveStep(
                draggedNodeId,
                destination.step.clickPayload.parentNodeToken,
                destination.step.clickPayload.branchIndex,
              );
            }
          }
        }}
        onNodeCustomEvent={(payload) => {
          switch (payload.key) {
            case WorkflowCanvasCustomEvent.Add:
              return addNodeAfter(payload.previousNodeToken as string);
            case WorkflowCanvasCustomEvent.AddToStartOfBranch:
              return addNodeAfterBranchStart(
                payload.parentNodeToken as string,
                payload.branchIndex as number,
              );
            case WorkflowCanvasCustomEvent.Select: {
              const moduleToken =
                keyedGraph[payload.parentStepToken as string]?.moduleToken ??
                keyedGraph[payload.nodeId].moduleToken ??
                null;
              const nodeToken = (payload.parentStepToken as string) ?? payload.nodeId;
              panTo(!runSteps && moduleToken ? moduleToken : nodeToken);

              const selectedStepToken = nodeToken.replace(`${moduleToken}-`, '');
              selectNode(selectedStepToken, {
                moduleToken: moduleToken === selectedStepToken ? null : moduleToken,
              });
              return;
            }
            case WorkflowCanvasCustomEvent.ToggleCollapsed: {
              if (keyedGraph[payload.nodeId].step.type === WorkflowStepType.Module) {
                if (prefixedModuleBlockSteps[payload.nodeId]) {
                  updateExpandedModuleStepTokens((current) =>
                    current.includes(payload.nodeId)
                      ? current.filter((ids) => ids !== payload.nodeId)
                      : [...current, payload.nodeId],
                  );
                }
              } else {
                toggleCollapsedNode(payload.nodeId);
              }
              return;
            }
          }
        }}
        onSurroundNodes={
          isShiftPressed
            ? (surrounded) => {
                copySteps(
                  surrounded
                    .flatMap<WorkflowStep | null>((token) => {
                      // TODO: the engine should support natively that node can have a param to config "surroundable" to avoid filtering here
                      const graphNode = keyedGraph[token];
                      switch (graphNode?.step?.type) {
                        case WorkflowStepType.Action:
                        case WorkflowStepType.Wait:
                        case WorkflowStepType.Placeholder:
                        case WorkflowStepType.Module:
                          return graphNode.step;
                        case WorkflowStepType.Conditional:
                        case WorkflowStepType.Parallel:
                        case WorkflowStepType.Loop: {
                          return collapsedBranchRootTokens.includes(token)
                            ? flattenSteps([graphNode.step])
                            : graphNode.step;
                        }
                        default:
                          return null;
                      }
                    })
                    .filter((step): step is WorkflowStep => step != null),
                );
              }
            : undefined
        }
      />
    </CanvasContainer>
  );
};

export default WorkflowCanvas;
